import { useEffect, useRef, useState, type MouseEvent, type RefObject } from 'react';
import type { BackendClient } from '@lumen/data';
import type { Platform } from '@lumen/platform';
import { Logo } from './Logo';
import { t } from '@lumen/platform';
import './landing.css';

/** Native app links. Empty until someone sets them; the page then shows "coming soon" instead (P-625). */
export interface Downloads {
  mac?: string | null;
  ios?: string | null;
}

const STEPS: { title: string; body: string }[] = [
  { title: t('Make an account'), body: t('An email address and a password. You get a workspace with a Getting Started page already in it.') },
  { title: t('Write'), body: t('Press Enter and type. “/” opens the block menu, Markdown shortcuts turn into real blocks, and Mod+K finds any page.') },
  { title: t('Bring people in'), body: t('Invite per page with full, edit, comment or view access — or publish a read-only link. Everything you do not share stays yours.') },
];

/** Capabilities that do not need a demo of their own, but should not go unmentioned. */
const ALSO = [
  'Headings, lists, to-dos, toggles',
  t('Callouts and quotes'),
  t('Code with syntax highlighting'),
  t('Mermaid diagrams'),
  'KaTeX equations',
  'Images, video, audio, PDFs',
  t('Bookmarks and embeds'),
  t('Columns and synced blocks'),
  t('Mentions and backlinks'),
  t('Inline and page comments'),
  t('Database templates and buttons'),
  'Formulas, relations, rollups',
  t('Version history and restore'),
  t('Trash for 30 days'),
  t('Import Markdown, HTML, CSV, .zip'),
  t('Export Markdown, HTML, CSV, PDF'),
  t('Publish to a public link'),
  'Light, dark and phone-sized',
];

/**
 * The public landing page: what a visitor without a session sees on `/` (P-620–P-627).
 * Visitors with a session never get here — `LumenApp` sends them straight to their workspace.
 */
export function Landing({ platform, backend, document, downloads }: { platform: Platform; backend: BackendClient; document: Document; downloads?: Downloads }) {
  const root = useRef<HTMLDivElement>(null);

  // There is no account to ask yet, so follow the system colour scheme (as the published site does).
  useEffect(() => {
    const apply = (dark: boolean) => (document.documentElement.dataset.theme = dark ? 'dark' : 'light');
    apply(platform.colorScheme.get() === 'dark');
    return platform.colorScheme.subscribe((c) => apply(c === 'dark'));
  }, [platform, document]);

  useReveals(root);
  const links = useDownloads(backend, downloads);

  // Real hrefs (openable in a new tab, crawlable), navigated in-app on a plain left click.
  const go = (path: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    platform.navigation.push(path);
  };

  return (
    <div className="lm-lp" data-testid="landing" ref={root}>
      <a className="lm-lp-skip" href="#lm-lp-main">
        {t('Skip to content')}
      </a>
      <header className="lm-lp-header">
        <a className="lm-lp-brand" href="/" onClick={go('/')} aria-label={t('Lumen, home')}>
          <Logo size={26} />
          <span className="lm-lp-wordmark">{t('Lumen')}</span>
        </a>
        <nav className="lm-lp-nav" aria-label={t('Sections')}>
          <a className="lm-lp-navlink" href="#see-it">
            {t('See it')}
          </a>
          <a className="lm-lp-navlink" href="#features">
            {t('Features')}
          </a>
          <a className="lm-lp-navlink" href="#how-it-works">
            {t('How it works')}
          </a>
          <a className="lm-lp-navlink" href="#hosting">
            {t('Hosting')}
          </a>
          <a className="lm-lp-navlink" href="#download">
            {t('Download')}
          </a>
        </nav>
        <div className="lm-lp-header-actions">
          <a className="lm-btn lm-btn-ghost lm-lp-btn" href="/login" onClick={go('/login')} data-testid="landing-header-login">
            {t('Log in')}
          </a>
          <a className="lm-btn lm-btn-primary lm-lp-btn lm-lp-btn-wide" href="/signup" onClick={go('/signup')} data-testid="landing-header-signup">
            {t('Create an account')}
          </a>
        </div>
      </header>

      <main id="lm-lp-main">
        <section className="lm-lp-hero">
          <div className="lm-lp-aurora" aria-hidden="true" />
          <div className="lm-lp-hero-text">
            <p className="lm-lp-eyebrow">
              <span className="lm-lp-dot" aria-hidden="true" />
              {t('Live at lumen.jeanluc.online')}
            </p>
            <h1 className="lm-lp-title">{t('One workspace for everything you write, plan and track.')}</h1>
            <p className="lm-lp-sub">
              {t('A block editor and a database in one, synced to every device the moment you type. Your pages live in the cloud on our own servers in Amsterdam — inside the EU, and nowhere else.')}
            </p>
            <div className="lm-lp-cta">
              <a className="lm-btn lm-btn-primary lm-btn-lg lm-lp-btn" href="/login" onClick={go('/login')} data-testid="landing-login">
                {t('Log in')}
              </a>
              <a className="lm-btn lm-btn-secondary lm-btn-lg lm-lp-btn" href="/signup" onClick={go('/signup')} data-testid="landing-signup">
                {t('Create an account')}
              </a>
            </div>
            <p className="lm-lp-note">{t('Free to start, no credit card. Nothing to install: open a browser and pick up where you left off.')}</p>
          </div>
          <HeroMockup />
          <ul className="lm-lp-trust" aria-label={t('At a glance')}>
            <li>{t('Amsterdam, EU region')}</li>
            <li>{t('Real-time sync')}</li>
            <li>{t('Keeps working offline')}</li>
            <li>{t('Export anything, any time')}</li>
          </ul>
        </section>

        <section className="lm-lp-section" id="see-it" aria-labelledby="lm-lp-video-heading">
          <div className="lm-lp-head lm-lp-reveal">
            <h2 className="lm-lp-h2" id="lm-lp-video-heading">
              {t('Half a minute of the real thing')}
            </h2>
            <p className="lm-lp-lead">
              {t('Recorded end to end in the actual app: writing, markdown shortcuts, the slash menu, a database in three views, two people in one page, and quick find. Captions only, no sound.')}
            </p>
          </div>
          <IntroVideo />
        </section>

        <section className="lm-lp-section" id="features" aria-labelledby="lm-lp-features-heading">
          <div className="lm-lp-head lm-lp-reveal">
            <h2 className="lm-lp-h2" id="lm-lp-features-heading">
              {t('A workspace that keeps up')}
            </h2>
            <p className="lm-lp-lead">{t('Every panel below is a thing Lumen does today, with an automated test behind it. No waiting list, no “coming soon”.')}</p>
          </div>
          <ul className="lm-lp-bento">
            <li className="lm-lp-cell lm-lp-cell-write lm-lp-reveal">
              <DemoWrite />
              <h3 className="lm-lp-cell-title">{t('Type instead of clicking')}</h3>
              <p className="lm-lp-cell-body">{t('Markdown shortcuts turn into real blocks as you type, and every action has a keyboard shortcut.')}</p>
            </li>
            <li className="lm-lp-cell lm-lp-cell-db lm-lp-reveal">
              <DemoDatabase />
              <h3 className="lm-lp-cell-title">{t('One database, six views')}</h3>
              <p className="lm-lp-cell-body">{t('Table, board, list, gallery, calendar and timeline over the same rows — each with its own filters, sorts, groups and calculations.')}</p>
            </li>
            <li className="lm-lp-cell lm-lp-cell-slash lm-lp-reveal">
              <DemoSlash />
              <h3 className="lm-lp-cell-title">{t('Press /')}</h3>
              <p className="lm-lp-cell-body">{t('Every block, one keystroke away.')}</p>
            </li>
            <li className="lm-lp-cell lm-lp-cell-collab lm-lp-reveal">
              <DemoCollab />
              <h3 className="lm-lp-cell-title">{t('Write together')}</h3>
              <p className="lm-lp-cell-body">{t('Live cursors, presence and comments.')}</p>
            </li>
            <li className="lm-lp-cell lm-lp-cell-sync lm-lp-reveal">
              <DemoSync />
              <h3 className="lm-lp-cell-title">{t('Synced everywhere, even when you are not')}</h3>
              <p className="lm-lp-cell-body">{t('Edits reach every device in real time. Lose the connection and you keep typing; it catches up by itself when you are back.')}</p>
            </li>
            <li className="lm-lp-cell lm-lp-cell-search lm-lp-reveal">
              <DemoSearch />
              <h3 className="lm-lp-cell-title">{t('Find anything')}</h3>
              <p className="lm-lp-cell-body">{t('Quick find searches titles and page text across the whole workspace, including pages you have never opened on this device.')}</p>
            </li>
          </ul>
          <ul className="lm-lp-chips lm-lp-reveal" aria-label={t('And also')}>
            {ALSO.map((c) => (
              <li className="lm-lp-chip" key={c}>
                {c}
              </li>
            ))}
          </ul>
        </section>

        <section className="lm-lp-section" id="how-it-works" aria-labelledby="lm-lp-how-heading">
          <div className="lm-lp-head lm-lp-reveal">
            <h2 className="lm-lp-h2" id="lm-lp-how-heading">
              {t('How it works')}
            </h2>
          </div>
          <ol className="lm-lp-steps">
            {STEPS.map((s, i) => (
              <li className="lm-lp-step lm-lp-reveal" key={s.title}>
                <span className="lm-lp-step-num" aria-hidden="true">
                  {i + 1}
                </span>
                <h3 className="lm-lp-cell-title">{s.title}</h3>
                <p className="lm-lp-cell-body">{s.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="lm-lp-section lm-lp-hosting" id="hosting" aria-labelledby="lm-lp-hosting-heading">
          <div className="lm-lp-head lm-lp-reveal">
            <h2 className="lm-lp-h2" id="lm-lp-hosting-heading">
              {t('Your pages live in the EU')}
            </h2>
            <p className="lm-lp-lead">
              {t('Lumen is hosted on our own servers in Amsterdam. Everything travels over HTTPS, access is decided page by page, and there are no analytics, no trackers and no third-party scripts on the pages you write.')}
            </p>
          </div>
          <ul className="lm-lp-facts">
            <li className="lm-lp-fact lm-lp-reveal">
              <DemoRegion />
              <strong className="lm-lp-fact-key">{t('Amsterdam, EU region')}</strong>
              <span className="lm-lp-cell-body">{t('One place. Your documents are not copied to another continent to make a page load faster.')}</span>
            </li>
            <li className="lm-lp-fact lm-lp-reveal">
              <span className="lm-lp-fact-num" aria-hidden="true">
                <ExportMark />
              </span>
              <strong className="lm-lp-fact-key">{t('Yours to take')}</strong>
              <span className="lm-lp-cell-body">{t('Export a page or the whole workspace as Markdown, HTML, CSV, PDF or a .zip, whenever you want.')}</span>
            </li>
            <li className="lm-lp-fact lm-lp-reveal">
              <span className="lm-lp-fact-num" aria-hidden="true">
                <ShieldMark />
              </span>
              <strong className="lm-lp-fact-key">{t('Shared on purpose')}</strong>
              <span className="lm-lp-cell-body">{t('Full, edit, comment or view access per page, guests by email, and publishing that you turn on yourself.')}</span>
            </li>
          </ul>
        </section>

        <section className="lm-lp-section" id="download" aria-labelledby="lm-lp-download-heading">
          <div className="lm-lp-head lm-lp-reveal">
            <h2 className="lm-lp-h2" id="lm-lp-download-heading">
              {t('Download')}
            </h2>
            <p className="lm-lp-lead">{t('The browser app is the whole product today, and it installs to your dock or home screen. Native shells are next.')}</p>
          </div>
          <ul className="lm-lp-downloads">
            <li className="lm-lp-cell lm-lp-download lm-lp-reveal" data-testid="download-web">
              <span className="lm-lp-download-mark" aria-hidden="true">
                <Logo size={30} />
              </span>
              <h3 className="lm-lp-cell-title">{t('Web')}</h3>
              <p className="lm-lp-cell-body">{t('Available now, on every platform, and it keeps working when the connection drops.')}</p>
              <a className="lm-btn lm-btn-primary lm-lp-btn" href="/login" onClick={go('/login')} data-testid="download-web-link">
                {t('Open Lumen')}
              </a>
            </li>
            <li className="lm-lp-cell lm-lp-download lm-lp-reveal" data-testid="download-mac">
              <span className="lm-lp-download-mark" aria-hidden="true">
                <LaptopMark />
              </span>
              <h3 className="lm-lp-cell-title">{t('Mac')}</h3>
              <p className="lm-lp-cell-body">{t('A desktop window around the same workspace, with your pages kept on the machine for offline use.')}</p>
              {links.mac ? (
                <a className="lm-btn lm-btn-secondary lm-lp-btn" href={links.mac} data-testid="download-mac-link">
                  {t('Download for Mac')}
                </a>
              ) : (
                <span className="lm-lp-soon" data-testid="download-mac-soon">
                  {t('Mac app coming soon')}
                </span>
              )}
            </li>
            <li className="lm-lp-cell lm-lp-download lm-lp-reveal" data-testid="download-ios">
              <span className="lm-lp-download-mark" aria-hidden="true">
                <PhoneMark />
              </span>
              <h3 className="lm-lp-cell-title">{t('iOS')}</h3>
              <p className="lm-lp-cell-body">{t('The same pages on a phone, in sync with everything else. The beta goes out through TestFlight first.')}</p>
              {links.ios ? (
                <a className="lm-btn lm-btn-secondary lm-lp-btn" href={links.ios} data-testid="download-ios-link">
                  {t('TestFlight')}
                </a>
              ) : (
                <span className="lm-lp-soon" data-testid="download-ios-soon">
                  {t('iOS — coming soon')}
                </span>
              )}
            </li>
          </ul>
        </section>

        <section className="lm-lp-section lm-lp-final">
          <div className="lm-lp-finalcard lm-lp-reveal">
            <div className="lm-lp-aurora lm-lp-aurora-small" aria-hidden="true" />
            <h2 className="lm-lp-h2">{t('Start a page.')}</h2>
            <p className="lm-lp-lead">{t('Your workspace is one click away, and it will be there on every device you open it on.')}</p>
            <div className="lm-lp-cta">
              <a className="lm-btn lm-btn-primary lm-btn-lg lm-lp-btn" href="/login" onClick={go('/login')} data-testid="landing-login-bottom">
                {t('Log in')}
              </a>
              <a className="lm-btn lm-btn-secondary lm-btn-lg lm-lp-btn" href="/signup" onClick={go('/signup')} data-testid="landing-signup-bottom">
                {t('Create an account')}
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="lm-lp-footer" data-testid="landing-footer">
        <div className="lm-lp-footer-brand">
          <Logo size={22} />
          <span className="lm-lp-wordmark">{t('Lumen')}</span>
          <span className="lm-lp-footer-tag">{t('Docs, notes and databases. Hosted in the EU.')}</span>
        </div>
        <nav className="lm-lp-footer-nav" aria-label={t('Footer')}>
          <a className="lm-lp-navlink" href="#see-it">
            {t('See it')}
          </a>
          <a className="lm-lp-navlink" href="#features">
            {t('Features')}
          </a>
          <a className="lm-lp-navlink" href="#how-it-works">
            {t('How it works')}
          </a>
          <a className="lm-lp-navlink" href="#hosting">
            {t('Hosting')}
          </a>
          <a className="lm-lp-navlink" href="#download">
            {t('Download')}
          </a>
          <a className="lm-lp-navlink" href="/login" onClick={go('/login')}>
            {t('Log in')}
          </a>
          <a className="lm-lp-navlink" href="/signup" onClick={go('/signup')}>
            {t('Create an account')}
          </a>
        </nav>
      </footer>
    </div>
  );
}

/**
 * Scroll-triggered reveals (P-627). One observer for the whole page; each element is released once it
 * has appeared. Without an observer (or under `prefers-reduced-motion`, which the CSS handles) every
 * element is simply visible.
 */
function useReveals(root: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const items = Array.from(el.querySelectorAll<HTMLElement>('.lm-lp-reveal'));
    if (typeof IntersectionObserver === 'undefined') {
      for (const i of items) i.dataset.in = 'true';
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          (e.target as HTMLElement).dataset.in = 'true';
          io.unobserve(e.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );
    for (const i of items) io.observe(i);
    return () => io.disconnect();
  }, [root]);
}

/**
 * Where the download links come from (P-625), most authoritative first:
 * the running server's `GET /api/config` (LUMEN_MAC_DOWNLOAD_URL / LUMEN_TESTFLIGHT_URL, so a link can
 * be added by setting an environment variable and restarting), then the build-time values. Neither set
 * means that platform shows "coming soon".
 */
function useDownloads(backend: BackendClient, fallback: Downloads | undefined): Downloads {
  const [links, setLinks] = useState<Downloads>(fallback ?? {});
  useEffect(() => {
    let live = true;
    backend
      .request<{ downloads?: Downloads }>(null, 'GET', '/api/config')
      .then((cfg) => {
        if (!live) return;
        setLinks({ mac: cfg.downloads?.mac || fallback?.mac || null, ios: cfg.downloads?.ios || fallback?.ios || null });
      })
      .catch(() => {
        /* no server, or an older one: the build-time values stand */
      });
    return () => {
      live = false;
    };
  }, [backend, fallback?.mac, fallback?.ios]);
  return links;
}

/**
 * The intro video (P-626): a short film made from recordings of the real app (`video/intro`, scenes
 * from `scripts/record-intro-scenes.ts`), with music and the sound of every click. It loops in place,
 * muted (the only way a browser lets it start by itself), and is only fetched once it comes near the
 * viewport, so a visitor who never scrolls that far pays nothing for it. The controls are "pause"
 * (WCAG 2.2.2: moving content that starts by itself needs a way to stop it), "sound" and "full screen".
 */
function IntroVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  const [near, setNear] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [sound, setSound] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        setNear(true);
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // The label follows what the visitor asked for, and the element's own events keep it honest
  // (a browser that refuses to start the loop reports it through `pause`).
  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (playing) el.pause();
    else void el.play().catch(() => setPlaying(false));
    setPlaying(!playing);
  };

  // Turning the sound on starts the film from the top, so the music and the story begin together.
  const toggleSound = () => {
    const el = ref.current;
    if (!el) return;
    const on = !sound;
    el.muted = !on;
    if (on) {
      el.currentTime = 0;
      void el.play().catch(() => setPlaying(false));
    }
    setSound(on);
  };

  const expand = () => {
    const el = ref.current;
    if (!el) return;
    el.controls = true;
    // iOS Safari only goes full screen through the video element's own API.
    const ios = el as HTMLVideoElement & { webkitEnterFullscreen?: () => void };
    if (el.requestFullscreen) void el.requestFullscreen();
    else ios.webkitEnterFullscreen?.();
  };

  return (
    <figure className="lm-lp-video lm-lp-reveal" data-testid="landing-video">
      <div className="lm-lp-videoframe">
        <div className="lm-lp-aurora lm-lp-aurora-small" aria-hidden="true" />
        <video
          ref={ref}
          className="lm-lp-video-el"
          {...(near ? { src: '/intro.mp4' } : {})}
          poster="/intro-poster.jpg"
          preload="none"
          muted
          loop
          autoPlay
          playsInline
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          data-testid="landing-video-el"
          aria-label={t('A short film made from recordings of Lumen: someone starts their own bakery. They write it down in a new page, make a plan with to-dos, break it into steps on a board, a friend joins and writes along live, they tick everything off, and they open on Saturday.')}
        />
      </div>
      <figcaption className="lm-lp-video-bar">
        <button type="button" className="lm-btn lm-btn-secondary" onClick={toggle} data-testid="landing-video-toggle">
          {playing ? t('Pause') : t('Play')}
        </button>
        <button type="button" className="lm-btn lm-btn-secondary" onClick={toggleSound} aria-pressed={sound} data-testid="landing-video-sound">
          {sound ? t('Sound off') : t('Sound on')}
        </button>
        <button type="button" className="lm-btn lm-btn-secondary" onClick={expand} data-testid="landing-video-expand">
          {t('Full screen')}
        </button>
      </figcaption>
    </figure>
  );
}

/**
 * The hero: the editor assembling itself. The sidebar rows arrive, the title types out, the blocks
 * drop in, a second cursor joins and a row is ticked off. Built from the same tokens as the real UI,
 * decorative, and completely still under `prefers-reduced-motion`.
 */
function HeroMockup() {
  return (
    <div className="lm-lp-shot" data-testid="landing-mockup" role="img" aria-label={t('The Lumen editor assembling a page called Product roadmap: to-dos, a callout and a database table, with a second person\'s cursor.')}>
      <div className="lm-lp-shot-frame" aria-hidden="true">
        <aside className="lm-lp-shot-sidebar">
          <div className="lm-lp-shot-ws">
            <span className="lm-lp-shot-wsicon">A</span>
            <span className="lm-lp-shot-wsname">{t('Acme HQ')}</span>
          </div>
          <div className="lm-lp-shot-rows">
            <div className="lm-lp-shot-row">
              <span className="lm-lp-shot-glyph">⌕</span> {t('Search')}
            </div>
            <div className="lm-lp-shot-row">
              <span className="lm-lp-shot-glyph">⌂</span> {t('Home')}
            </div>
            <div className="lm-lp-shot-label">{t('Teamspaces')}</div>
            <div className="lm-lp-shot-row">
              <span className="lm-lp-shot-glyph">📣</span> {t('Marketing')}
            </div>
            <div className="lm-lp-shot-label">{t('Private')}</div>
            <div className="lm-lp-shot-row lm-lp-shot-row-active">
              <span className="lm-lp-shot-glyph">🚀</span> {t('Product roadmap')}
            </div>
            <div className="lm-lp-shot-row">
              <span className="lm-lp-shot-glyph">📓</span> {t('Research notes')}
            </div>
            <div className="lm-lp-shot-row">
              <span className="lm-lp-shot-glyph">📅</span> {t('Weekly review')}
            </div>
          </div>
        </aside>
        <div className="lm-lp-shot-main">
          <div className="lm-lp-shot-topbar">
            <span className="lm-lp-shot-crumb">{t('Private')}</span>
            <span className="lm-lp-shot-sep">/</span>
            <span className="lm-lp-shot-crumb lm-lp-shot-crumb-current">{t('🚀 Product roadmap')}</span>
            <span className="lm-lp-shot-spacer" />
            <span className="lm-lp-shot-avatars">
              <span className="lm-lp-shot-avatar lm-lp-shot-avatar-a">A</span>
              <span className="lm-lp-shot-avatar lm-lp-shot-avatar-b">M</span>
            </span>
            <span className="lm-lp-shot-share">{t('Share')}</span>
          </div>
          <div className="lm-lp-shot-page">
            <div className="lm-lp-shot-icon">🚀</div>
            <div className="lm-lp-shot-title">
              <span className="lm-lp-typed">{t('Product roadmap')}</span>
            </div>
            <div className="lm-lp-shot-blocks">
              <div className="lm-lp-shot-h2">{t('This quarter')}</div>
              <p className="lm-lp-shot-text">
                {t('Three slices, one owner each. The board view groups by')} <span className="lm-lp-shot-code">{t('Status')}</span>.
                <span className="lm-lp-shot-caret">
                  <span className="lm-lp-shot-caret-name">{t('Mara')}</span>
                </span>
              </p>
              <div className="lm-lp-shot-todo">
                <span className="lm-lp-shot-check lm-lp-shot-check-on">✓</span>
                <span className="lm-lp-shot-done">{t('Collect feedback from the beta group')}</span>
              </div>
              <div className="lm-lp-shot-todo">
                <span className="lm-lp-shot-check lm-lp-shot-check-tick">✓</span>
                <span>{t('Decide on the offline story')}</span>
              </div>
              <div className="lm-lp-shot-todo">
                <span className="lm-lp-shot-check" />
                <span>{t('Write the migration guide')}</span>
              </div>
              <div className="lm-lp-shot-callout">
                <span className="lm-lp-shot-callout-icon">💡</span>
                <span>{t('Everything here syncs live. Try it with a second browser window.')}</span>
              </div>
              <div className="lm-lp-shot-db">
                <div className="lm-lp-shot-tabs">
                  <span className="lm-lp-shot-tab lm-lp-shot-tab-active">{t('Table')}</span>
                  <span className="lm-lp-shot-tab">{t('Board')}</span>
                  <span className="lm-lp-shot-tab">{t('Calendar')}</span>
                </div>
                <div className="lm-lp-shot-tr lm-lp-shot-th">
                  <span>{t('Name')}</span>
                  <span>{t('Status')}</span>
                  <span>{t('Owner')}</span>
                </div>
                <div className="lm-lp-shot-tr">
                  <span>{t('Offline sync')}</span>
                  <span>
                    <span className="lm-lp-shot-chip lm-lp-shot-chip-green">{t('Done')}</span>
                  </span>
                  <span>{t('Ada')}</span>
                </div>
                <div className="lm-lp-shot-tr">
                  <span>{t('Timeline view')}</span>
                  <span>
                    <span className="lm-lp-shot-chip lm-lp-shot-chip-blue">{t('In progress')}</span>
                  </span>
                  <span>{t('Mara')}</span>
                </div>
                <div className="lm-lp-shot-tr">
                  <span>{t('EU hosting')}</span>
                  <span>
                    <span className="lm-lp-shot-chip lm-lp-shot-chip-yellow">{t('Next up')}</span>
                  </span>
                  <span>{t('Grace')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────── Bento mini-demos. All decorative, all paused under prefers-reduced-motion. ───────── */

function DemoWrite() {
  return (
    <div className="lm-lp-demo lm-lp-demo-write" aria-hidden="true">
      <div className="lm-lp-demo-line lm-lp-demo-md">
        <span className="lm-lp-demo-typed">{t('## This quarter')}</span>
        <span className="lm-lp-demo-caret" />
      </div>
      <div className="lm-lp-demo-line lm-lp-demo-h">{t('This quarter')}</div>
      <div className="lm-lp-demo-line lm-lp-demo-dim">{t('[] Ship the editor')}</div>
      <div className="lm-lp-demo-line lm-lp-demo-dim">{t('> Toggle for the details')}</div>
    </div>
  );
}

function DemoSlash() {
  return (
    <div className="lm-lp-demo lm-lp-demo-slash" aria-hidden="true">
      <div className="lm-lp-demo-menu">
        <span className="lm-lp-demo-pick" />
        <span className="lm-lp-demo-item">{t('Heading 1')}</span>
        <span className="lm-lp-demo-item">{t('To-do list')}</span>
        <span className="lm-lp-demo-item">{t('Callout')}</span>
        <span className="lm-lp-demo-item">{t('Table')}</span>
      </div>
    </div>
  );
}

function DemoDatabase() {
  return (
    <div className="lm-lp-demo lm-lp-demo-db" aria-hidden="true">
      <div className="lm-lp-demo-tabs">
        <span className="lm-lp-demo-tab lm-lp-demo-tab-a">{t('Table')}</span>
        <span className="lm-lp-demo-tab lm-lp-demo-tab-b">{t('Board')}</span>
        <span className="lm-lp-demo-tab lm-lp-demo-tab-c">{t('Calendar')}</span>
      </div>
      <div className="lm-lp-demo-stage">
        <div className="lm-lp-demo-pane lm-lp-demo-pane-a">
          {[t('Offline sync'), t('Timeline view'), 'EU hosting', t('Mac app')].map((r) => (
            <span className="lm-lp-demo-trow" key={r}>
              <span>{r}</span>
              <span className="lm-lp-demo-pill" />
            </span>
          ))}
        </div>
        <div className="lm-lp-demo-pane lm-lp-demo-pane-b">
          {[2, 1, 2].map((n, i) => (
            <span className="lm-lp-demo-col" key={i}>
              <span className="lm-lp-demo-colhead" />
              {Array.from({ length: n }, (_, j) => (
                <span className="lm-lp-demo-card" key={j} />
              ))}
            </span>
          ))}
        </div>
        <div className="lm-lp-demo-pane lm-lp-demo-pane-c">
          {Array.from({ length: 21 }, (_, i) => (
            <span className={`lm-lp-demo-day${i === 4 || i === 9 || i === 16 ? ' lm-lp-demo-day-on' : ''}`} key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

function DemoCollab() {
  return (
    <div className="lm-lp-demo lm-lp-demo-collab" aria-hidden="true">
      <div className="lm-lp-demo-line lm-lp-demo-dim">
        {t('Ship on Friday')}
        <span className="lm-lp-demo-cursor lm-lp-demo-cursor-a">
          <span>{t('Ada')}</span>
        </span>
      </div>
      <div className="lm-lp-demo-line lm-lp-demo-dim">
        {t('Everyone agrees')}
        <span className="lm-lp-demo-cursor lm-lp-demo-cursor-b">
          <span>{t('Mara')}</span>
        </span>
      </div>
    </div>
  );
}

function DemoSync() {
  return (
    <div className="lm-lp-demo lm-lp-demo-sync" aria-hidden="true">
      <span className="lm-lp-demo-state lm-lp-demo-state-a">{t('Offline — saved here')}</span>
      <span className="lm-lp-demo-state lm-lp-demo-state-b">{t('Reconnecting…')}</span>
      <span className="lm-lp-demo-state lm-lp-demo-state-c">{t('Synced to every device')}</span>
    </div>
  );
}

function DemoSearch() {
  return (
    <div className="lm-lp-demo lm-lp-demo-search" aria-hidden="true">
      <div className="lm-lp-demo-field">
        <span className="lm-lp-demo-glass">⌕</span>
        <span className="lm-lp-demo-typed lm-lp-demo-query">{t('roadmap')}</span>
        <span className="lm-lp-demo-caret" />
      </div>
      <div className="lm-lp-demo-result lm-lp-demo-result-a">
        <span className="lm-lp-demo-mark">{t('Roadmap')}</span> {t('— Private')}
      </div>
      <div className="lm-lp-demo-result lm-lp-demo-result-b">
        Q4 <span className="lm-lp-demo-mark">{t('roadmap')}</span> {t('review — Marketing')}
      </div>
    </div>
  );
}

/** Platform marks for the download cards: our own outlines, so they render the same everywhere. */
function LaptopMark() {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
      <rect x="5" y="7" width="22" height="14" rx="2" />
      <path d="M2.5 24.5h27" strokeLinecap="round" />
    </svg>
  );
}

function PhoneMark() {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
      <rect x="10" y="3.5" width="12" height="25" rx="3" />
      <path d="M14 25.5h4" strokeLinecap="round" />
    </svg>
  );
}

function ExportMark() {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 6v13M10.5 13.5 16 19l5.5-5.5" />
      <path d="M7 23.5h18" />
    </svg>
  );
}

function ShieldMark() {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 5.5 7 9v7c0 5.2 3.7 9 9 10.5 5.3-1.5 9-5.3 9-10.5V9l-9-3.5Z" />
      <path d="m12.3 16.1 2.6 2.6 4.8-5" />
    </svg>
  );
}

function DemoRegion() {
  return (
    <span className="lm-lp-demo-region" aria-hidden="true">
      <span className="lm-lp-demo-ping" />
      <span className="lm-lp-demo-node" />
    </span>
  );
}
